import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle2, XCircle, Activity, Clock, AlertTriangle, Server, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GaugeCircle({ value, target, size = 120 }) {
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(value, 100);
  const offset = circ - (pct / 100) * circ;
  const meetsTarget = value >= target;
  const color = meetsTarget ? '#16A34A' : value >= target - 1 ? '#D97706' : '#DC2626';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={8} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold" style={{ color }}>{value.toFixed(2)}%</span>
        <span className="text-xs text-muted-foreground">meta: {target}%</span>
      </div>
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function SLADashboardPage() {
  const [slaConfigs, setSlaConfigs] = useState([]);
  const [slaStatus, setSlaStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sla-configs`, { headers: getAuthHeader() });
      const configs = res.data || [];
      setSlaConfigs(configs);

      // Fetch status for each SLA config
      const statusMap = {};
      await Promise.all(configs.map(async (sla) => {
        try {
          const statusRes = await axios.get(`${API}/sla-configs/${sla.id}/status?days=${period}`, { headers: getAuthHeader() });
          statusMap[sla.id] = statusRes.data;
        } catch (err) {
          statusMap[sla.id] = { status: 'error', summary: {} };
        }
      }));
      setSlaStatus(statusMap);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [getAuthHeader, period]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-40" /><Skeleton className="h-[400px] w-full" /></div>;

  if (slaConfigs.length === 0) {
    return (
      <div className="space-y-6">
        <h2 className="text-lg font-semibold">Dashboard de SLA</h2>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Activity className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground font-medium">Nenhum SLA configurado</p>
            <p className="text-sm text-muted-foreground mt-1">Configure SLAs em Gerenciamento &rarr; SLA</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sla-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Dashboard de SLA</h2>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 dias</SelectItem>
            <SelectItem value="30">30 dias</SelectItem>
            <SelectItem value="60">60 dias</SelectItem>
            <SelectItem value="90">90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* SLA Cards */}
      {slaConfigs.map(sla => {
        const st = slaStatus[sla.id];
        const sum = st?.summary || {};
        const hosts = st?.hosts || [];
        const meetsAvail = sum.meets_availability_target;
        const meetsMttr = sum.meets_mttr_target;

        return (
          <Card key={sla.id} className={`border-l-4 ${meetsAvail && meetsMttr ? 'border-l-emerald-500' : meetsAvail === false ? 'border-l-red-500' : 'border-l-amber-500'}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  {meetsAvail && meetsMttr ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
                  {sla.name}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant={meetsAvail && meetsMttr ? 'default' : 'destructive'} className={meetsAvail && meetsMttr ? 'bg-emerald-500' : ''}>
                    {meetsAvail && meetsMttr ? 'Dentro do SLA' : 'Fora do SLA'}
                  </Badge>
                  <Badge variant="outline" className="text-xs">{sum.compliance_rate || 0}% compliance</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Gauge */}
                <div className="flex flex-col items-center justify-center">
                  <GaugeCircle value={sum.availability || 0} target={sla.target_availability || 99} size={140} />
                  <p className="text-xs text-muted-foreground mt-2">Disponibilidade</p>
                </div>

                {/* Metrics */}
                <div className="lg:col-span-2 grid grid-cols-2 gap-3">
                  <Card className={`border ${meetsMttr ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'}`}>
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">MTTR</p>
                      <p className="text-xl font-bold">{sum.mttr_hours || 0}h</p>
                      <p className="text-xs text-muted-foreground">meta: {sla.target_mttr_hours}h</p>
                    </CardContent>
                  </Card>
                  <Card className="border">
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Incidentes</p>
                      <p className="text-xl font-bold">{sum.total_incidents || 0}</p>
                      <p className="text-xs text-muted-foreground">no período</p>
                    </CardContent>
                  </Card>
                  <Card className="border">
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Downtime Total</p>
                      <p className="text-xl font-bold">{formatDuration(sum.total_downtime_seconds || 0)}</p>
                    </CardContent>
                  </Card>
                  <Card className="border">
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Hosts no SLA</p>
                      <p className="text-xl font-bold">{sum.hosts_meeting_target || 0}<span className="text-sm font-normal text-muted-foreground">/{sum.total_hosts || 0}</span></p>
                    </CardContent>
                  </Card>
                </div>

                {/* Worst hosts */}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Hosts com menor disponibilidade</p>
                  <div className="space-y-1">
                    {hosts.slice(0, 5).map((h, i) => (
                      <div key={h.hostid} className="flex items-center justify-between text-sm p-1.5 rounded border">
                        <span className="truncate flex-1 mr-2">{h.name}</span>
                        <span className={`font-mono font-bold text-xs ${h.meets_target ? 'text-emerald-600' : 'text-red-600'}`}>
                          {h.availability.toFixed(2)}%
                        </span>
                      </div>
                    ))}
                    {hosts.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">Sem dados</p>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
